import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'uplisting-frontend/config/environment';
import Service from 'ember-simple-auth/services/session';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { openPageWithAmplitudeDeviceId } from 'uplisting-frontend/utils/analytics';

interface IError {
  status: '401' | '402' | '403' | '404' | '409' | '423';
}

interface IFetchError {
  status?: number;
  errors: IError[];
}

function isErrorOfStatus(err: IFetchError, status: number): boolean {
  return (
    err.errors?.some((error) => error.status === `${status}`) ||
    err?.status === status
  );
}

export default class SessionService extends Service {
  @service router!: Services['router'];
  @service sentry!: Services['sentry'];
  @service crisp!: Services['crisp'];
  @service userflow!: Services['userflow'];
  @service('repositories/user') userRepository!: Services['repositories/user'];
  @service featureFlags!: Services['feature-flags'];

  @tracked currentUser?: ModelRegistry['user'];

  public async fetchCurrentUser(params = { reload: false }): Promise<void> {
    if ((this.currentUser || !this.isAuthenticated) && !params.reload) {
      return;
    }

    try {
      const user = await this.userRepository.findRecord('me', {
        include: 'customer,permissions',
        reload: params.reload,
      });

      this.setUser(user);
    } catch (err) {
      const notAuthorized = this.isAuthenticationError(err as IFetchError);
      const isConflictError = this.isConflictError(err as IFetchError);

      if (notAuthorized) {
        this.handleNotAuthorized();
      } else if (isConflictError) {
        this.router.transitionTo('account.verify');
      } else {
        this.sentry.captureException(err);
      }
    }
  }

  public setUser(user: ModelRegistry['user']): void {
    this.currentUser = user;

    this.sentry.configure({
      id: user.id,
      email: user.email,
    });
  }

  public handleInvalidation() {
    // no-op
    // By default this will reload the app, but we don't want that, we just want to clear local storage
  }

  public handleAuthentication(): void {
    // no-op
    // We don't want to redirect to the routeAfterAuthentication
  }

  public handleNotAuthorized() {
    this.crisp.reset();
    this.userflow.reset();
    this.invalidate();

    openPageWithAmplitudeDeviceId(config.auth.loginUrl);
  }

  public handleLogout() {
    this.featureFlags.shutdown();
    this.crisp.reset();
    this.userflow.reset();
    this.invalidate();

    window.open(config.auth.logoutUrl, '_self');
  }

  private isAuthenticationError(err: IFetchError): boolean {
    return isErrorOfStatus(err, 401);
  }

  private isConflictError(err: IFetchError): boolean {
    return isErrorOfStatus(err, 409);
  }
}

declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
